import { css } from '@emotion/core';
import { css as cssClassName } from 'emotion';
import styled from '@emotion/styled';
import { FocusButton } from 'client/components/FocusButton';
import { ControlledFieldDatePicker } from 'client/components/FieldDatePicker';
import { FieldLabel } from 'components/FieldLabel';
import { SM_SCREEN_MAX } from 'modules/Styleguide';
import DropdownButton from 'react-bootstrap/lib/DropdownButton';
import MenuItem from 'react-bootstrap/lib/MenuItem';
import { Button } from 'shared/Button/NewButton';

export const DatesContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 16px 0 50px;
  p {
    margin-bottom: 0;
    color: #5f6369;
  }
  button {
    padding: 0;
  }
  @media (max-width: ${SM_SCREEN_MAX}px) {
    display: block;
  }
`;

export const StyledDatePicker = styled(ControlledFieldDatePicker)`
  &:first-of-type {
    padding: 0 16px 0 0;
  }
  button {
    padding: 0;
    &:focus > div {
      border: 1px solid #702cd5;
    }
    > div {
      border: 1px solid transparent;
      &:focus {
        outline: 0;
      }
    }
  }
  @media (max-width: ${SM_SCREEN_MAX}px) {
    &:first-of-type {
      padding: 0;
    }
    button {
      width: 100%;
    }
  }
`;

export const InteractiveSectionContainer = styled.div`
  margin: 36px 40px 40px 40px;
  padding-bottom: 40px;
  border-bottom: 2px solid #dfe0e1;
`;

export const Title = styled.h3`
  display: inline-block;
  margin: 0 0 20px;
  padding: 0;
  font-weight: bold;
  font-size: 2rem;
  line-height: 23px;
`;

export const Optional = styled.span`
  color: #7f8287;
  font-weight: normal;
  font-size: 1.4rem;
`;

export const PollTypeStyles = css`
  width: 360px;
  margin: 16px 0;
  color: #292f36;
  font-weight: bold;
  font-size: 1.8rem;
`;

export const OpenModalButton = styled(Button)`
  width: 200px;
  margin-top: 4px;
`;

export const TopBarContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const ThreeDotMenuContainer = styled(DropdownButton)`
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  &:hover,
  &:active,
  &:focus,
  &:active:focus {
    background: transparent !important;
    box-shadow: none !important;
  }
  svg > g > g {
    fill: #c9cbcd;
  }
  &:hover {
    svg > g > g {
      fill: #999;
    }
  }
`;

export const ThreeDotMenuItem = styled(MenuItem)<{ isWarning?: boolean }>`
  /* overrides react-bootstrap styling */
  a {
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 2.25;
    padding: 3px 20px;
    width: 240px;
    ${({ isWarning }) => (isWarning ? `color: #e54751;` : 'color: #333;')};
    align-items: center;
    display: flex;
    text-decoration: none;
  }
`;

export const CircularIconContainer = styled.div`
  width: 18px;
  height: 18px;
  padding: 4px;
  border-radius: 100%;
  background-color: #cccdcf;
`;

export const StatusText = styled.p`
  margin-bottom: 16px;
  color: #7f8287;
  font-weight: bold;
`;

export const FormContainer = styled.div`
  padding: 35px;
  @media (max-width: ${SM_SCREEN_MAX}px) {
    padding: 0;
  }
`;

export const RoundedCard = styled.div`
  padding: 36px;
  border-radius: 6px;
  background-color: white;
`;

export const BannerTitle = styled.h3`
  margin: 0 0 8px;
  color: #292f36;
`;

export const CopyContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0;
`;

export const OptionsContainer = styled.div`
  margin-bottom: 36px;
`;

export const OptionsInputsGridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 30px;
  margin-bottom: 1.6rem;
`;

export const ModalTitle = styled.h3`
  margin: 0 0 40px;
  line-height: 2.8rem;
  text-align: center;
`;

export const ConfirmationButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const SubmitButton = styled(Button)`
  display: flex;
  width: 220px;
  margin-left: 0;
`;

export const QuestionTitle = styled.p`
  margin: 22px 0 20px;
  font-weight: bold;
  font-size: 1.6rem;
`;

export const SaveDraftButton = styled.button`
  display: flex;
  margin-right: 44px;
  margin-left: auto;
  padding: 20px;
  color: #5000b9;
  font-weight: bold;
  font-size: 1.6rem;
  line-height: 24px;
  &:hover {
    color: #702cd5;
  }
  &:active {
    color: #3e0d88;
  }
  &[disabled] {
    color: #7f8287;
  }
`;

// using !important to override bootstrap modal styling
export const ErrorMessage = styled.p`
  color: #9a201c !important;
`;

export const PlusIcon = styled.span`
  position: relative;
  display: inline-block;
  width: 20px;
  height: 20px;
  margin: 4px 8px 4px 0;
  border: 1px solid #000;
  border-radius: 100%;
  color: #fff;
  vertical-align: middle;
  background: #5000b9;

  &:before,
  &:after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: #fff;
    content: '';
  }
  &:before {
    width: 2px;
    margin: 3px auto;
  }
  &:after {
    height: 2px;
    margin: auto 2px;
  }
`;

export const AddOptionButton = styled(FocusButton)`
  display: flex;
  align-items: center;
  color: #5000b9;
  font-weight: bold;
  font-size: 1.6rem;
  &:hover {
    color: #702cd5;
  }
  &:active {
    color: #3e0d88;
  }
`;

export const RemoveOptionButton = styled(FocusButton)`
  display: block;
  height: 46px;
  margin-bottom: 1.6rem;
  padding: 1px 6px;
`;

export const StyledFieldLabel = styled(FieldLabel)`
  margin-bottom: 8px;
  color: #292f36;
  font-weight: bold;
  font-size: 1.8rem;
`;

export const DeleteModalContainer = styled.div`
  width: 100%;
  max-width: 575px;
  margin: auto;
`;

export const DeleteModalCopy = styled.div`
  max-width: 485px;
  margin: 0 auto 32px auto;
`;

export const BootstrapModifiers = cssClassName`
  .btn-group.open .dropdown-toggle {
    background: transparent;
    box-shadow: none;
  }
  .modal.fade .modal-dialog {
    @media (max-width: ${SM_SCREEN_MAX}px) {
      width: 100%;
      margin: auto 32px;
    }
  }
  .modal-content p,
  .modal-content li {
    margin-bottom: 16px;
  }
  .modal-content,
  .modal-content p,
  .modal-content li {
    color: #7f8287;
  }
  .modal-content h3,
  legend {
    color: #292f36;
  }
`;

export const BootstrapMarginModifiers = cssClassName`
  .modal.fade .modal-dialog {
    width: 800px;
    margin: 5vh auto 0 auto;
  }
`;
